/**
 * Public-path allow-list helper (ABG-001 / Phase E1.3).
 *
 * `createApiClient`'s pre-flight defaults to **default-deny** for authenticated
 * requests: when no bearer token is available, the request is rejected locally
 * with `NoAuthTokenError`. The allow-list is the escape hatch for the requests a
 * client legitimately makes with NO session.
 *
 * Match strategy: **string-prefix on the request URL AS PASSED to the client** —
 * `reqConfig.url`, RELATIVE to the client's `baseURL`, which is never joined in —
 * after stripping query string + hash fragment. Trailing slash on the pattern
 * enables sub-path-only match; absence of trailing slash matches the path itself
 * AND its `/…` sub-paths.
 *
 * 🔴 That relativity is the whole design constraint. The fleet's clients sit on
 * `''` (callers pass `/api/v1/…`), `/api` (callers pass `/v1/…`), `/api/v1`, or a
 * domain base such as `/api/v1/planner`, so one pattern means a different server
 * route under each — and a pattern written for one base matches nothing under
 * the others. An allow-list is therefore a property of ONE client, stated where
 * that client is constructed, in the exact shape its callers request.
 *
 * Standards reference: `GUIDES/shared/standards/auth-bootstrap.md` (ABG-001).
 *
 * @module @puffin-core/api-client/publicPaths
 */

/**
 * Default allow-list applied when `createApiClient({ publicPaths })` is omitted.
 * Frozen so accidental in-place mutation by a caller is impossible.
 *
 * 🔴 UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-17 (2026-09-24) — MEASURED, then cut to
 * what is requested. This list used to carry nineteen entries (`/auth/login`,
 * `/auth/refresh`, the password-reset and MFA steps, `/auth/cross-app/exchange`,
 * `/.well-known/` …). A census of every `getToken`-bearing client in the fleet
 * found that exactly ONE of them was ever matched by a real request: `/health`,
 * which planner-web's and prep-web's platform dashboards request through their
 * domain clients (`/api/v1/planner` → `/v1/planner/health`, `/api/v1/prep` →
 * `/v1/prep/health`). The other eighteen could not match anything — auth calls
 * travel through `@puffin-core/auth-client`'s transport (`authRequired: false`)
 * or through a client with its own list, and every client that does request an
 * auth path spells it with a tenant/platform segment (`/auth/tenant/login`) the
 * bare entries lack. `/auth/refresh` matched no ROUTE anywhere at all.
 *
 * A dead entry is not harmless: it reads as coverage, and the next author routes
 * a pre-session call through a client "because the default covers auth" — and
 * the request is refused on the device, which looks like an API outage. That is
 * how palm-native's MFA sign-in and console's passkey sign-in shipped refused.
 *
 * `/health` stays because it is requested: a health probe under a client's own
 * base is public wherever an API serves one. The sub-path rule covers
 * `/health/live` and `/health/ready`. The
 * `apiClientPublicPathsLive` cross-app contract proves every entry here, and in
 * every client-level list, resolves to a mounted UNAUTHENTICATED route on the API
 * that client targets — so an entry that stops matching goes red.
 */
export const DEFAULT_PUBLIC_PATHS = Object.freeze(['/health']);

/**
 * Strip the query string + hash fragment from a URL pathname, returning just
 * the path portion. Does not crash on null / undefined / non-string input —
 * caller-defensive.
 *
 * @param {string} url
 * @returns {string}
 */
function stripQueryAndHash(url) {
  // `url` is guaranteed to be a non-empty string here: the sole caller,
  // `isPublicPath`, validates `typeof url === 'string' && url.length > 0`
  // before invoking this helper. A redundant type/empty guard would therefore
  // be unreachable dead code.
  const queryIdx = url.indexOf('?');
  const hashIdx = url.indexOf('#');
  let endIdx = url.length;
  if (queryIdx >= 0 && queryIdx < endIdx) endIdx = queryIdx;
  if (hashIdx >= 0 && hashIdx < endIdx) endIdx = hashIdx;
  return url.slice(0, endIdx);
}

/**
 * Is the URL path a member of the public allow-list?
 *
 * Match rule per pattern entry:
 * - Pattern ends with `/` (e.g. `/auth/authenticator/status/`): match if path
 *   STARTS WITH the pattern (sub-path match).
 * - Pattern does not end with `/` (e.g. `/health`): match if path EQUALS the
 *   pattern OR path STARTS WITH `${pattern}/` (defensive: `/health/live`
 *   should match `/health` if the user wrote it without the trailing slash —
 *   but `/healthcare` MUST NOT match — hence the `/` boundary).
 *
 * @param {string|null|undefined} url
 * @param {ReadonlyArray<string>|null|undefined} allowList - read, never changed (`DEFAULT_PUBLIC_PATHS` is frozen)
 * @returns {boolean}
 */
export function isPublicPath(url, allowList) {
  if (typeof url !== 'string' || url.length === 0) return false;
  if (!Array.isArray(allowList)) return false;
  const path = stripQueryAndHash(url);
  if (!path) return false;
  for (const pattern of allowList) {
    if (typeof pattern !== 'string' || pattern.length === 0) continue;
    if (pattern.endsWith('/')) {
      if (path.startsWith(pattern)) return true;
    } else if (path === pattern || path.startsWith(`${pattern}/`)) {
      return true;
    }
  }
  return false;
}
